import React, { useState, useEffect } from 'react';
import { UserPlus, Search, Edit2, Trash2, CheckCircle, XCircle, Building2, Filter } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';

interface User {
  id: string;
  email: string;
  username: string;
  nombre: string;
  apellidos: string;
  departamento_id: string | null;
  role: 'administrador' | 'usuario_avanzado' | 'usuario_estandar';
  estado: 'activo' | 'inactivo';
  created_at: string;
  departamento?: {
    nombre: string;
  };
  grupo_id: string;
  grupo?: {
    nombre: string;
    role: 'administrador' | 'usuario_avanzado' | 'usuario_estandar';
  };
  empresa_id?: string;
  empresa?: {
    nombre: string;
    codigo_fiscal?: string;
  };
}

interface Department {
  id: string;
  nombre: string;
}

interface UserGroup {
  id: string;
  nombre: string;
  role: 'administrador' | 'usuario_avanzado' | 'usuario_estandar';
}

interface Empresa {
  id: string;
  nombre: string;
  codigo_fiscal: string;
}

export default function Users() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [empresaFilter, setEmpresaFilter] = useState('');
  const [cifFilter, setCifFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState({
    email: '',
    username: '',
    password: '',
    nombre: '',
    apellidos: '',
    departamento_id: '',
    grupo_id: '',
    empresa_id: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedGroupRole, setSelectedGroupRole] = useState<string>('usuario_estandar');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
      fetchDepartments();
      fetchUserGroups();
      fetchEmpresas();
    }
  }, [currentUser]);

  const fetchUsers = async () => {
    try {
      const { data: users, error } = await supabase
        .from('usuarios')
        .select(`
          *,
          departamento:departamentos(nombre),
          grupo:grupos_usuarios(nombre, role),
          empresa:empresas!usuarios_empresa_id_fkey(nombre, codigo_fiscal)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Error al cargar los usuarios');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data: departments, error } = await supabase
        .from('departamentos')
        .select('*')
        .order('nombre');

      if (error) throw error;
      setDepartments(departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const { data: groups, error } = await supabase
        .from('grupos_usuarios')
        .select('*')
        .order('nombre');

      if (error) throw error;
      setUserGroups(groups || []);

      // Set default group for new users
      if (groups && groups.length > 0) {
        const defaultGroup = groups.find(g => g.nombre === 'Grupo Estándar') || groups[0];
        setNewUser(prev => ({ ...prev, grupo_id: defaultGroup.id }));
        setSelectedGroupRole(defaultGroup.role);
      }
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  };

  const fetchEmpresas = async () => {
    try {
      const { data: empresasData, error } = await supabase
        .from('empresas')
        .select('id, nombre, codigo_fiscal')
        .eq('estado', 'activo')
        .order('nombre');

      if (error) throw error;
      setEmpresas(empresasData || []);
    } catch (error) {
      console.error('Error fetching empresas:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        const updateData = {
          nombre: newUser.nombre,
          apellidos: newUser.apellidos,
          departamento_id: newUser.departamento_id || null,
          grupo_id: newUser.grupo_id,
          empresa_id: newUser.empresa_id || null,
          username: newUser.username
        };

        const { error } = await supabase
          .from('usuarios')
          .update(updateData)
          .eq('id', editingUser.id);

        if (error) throw error;
        setSuccess('Usuario actualizado correctamente');
      } else {
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: newUser.email,
          password: newUser.password,
          options: {
            data: {
              nombre: newUser.nombre,
              username: newUser.username
            }
          }
        });

        if (authError) throw authError;
        if (!authData.user) throw new Error('No se pudo crear el usuario');

        const { error: dbError } = await supabase.from('usuarios').insert([{
          id: authData.user.id,
          email: newUser.email,
          username: newUser.username,
          nombre: newUser.nombre,
          apellidos: newUser.apellidos,
          departamento_id: newUser.departamento_id || null,
          grupo_id: newUser.grupo_id,
          empresa_id: newUser.empresa_id || null,
          estado: 'activo'
        }]);

        if (dbError) throw dbError;
        setSuccess('Usuario creado correctamente');
      }

      setShowModal(false);
      setEditingUser(null);
      setNewUser({
        email: '',
        username: '',
        password: '',
        nombre: '',
        apellidos: '',
        departamento_id: '',
        grupo_id: userGroups.find(g => g.nombre === 'Grupo Estándar')?.id || userGroups[0]?.id || '',
        empresa_id: ''
      });
      fetchUsers();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setNewUser({
      email: user.email,
      username: user.username || '',
      password: '',
      nombre: user.nombre,
      apellidos: user.apellidos,
      departamento_id: user.departamento_id || '',
      grupo_id: user.grupo_id,
      empresa_id: user.empresa_id || ''
    });
    // Set the selected group's role
    const group = userGroups.find(g => g.id === user.grupo_id);
    if (group) {
      setSelectedGroupRole(group.role);
    }
    setShowModal(true);
  };

  const handleUpdateUserStatus = async (userId: string, newStatus: 'activo' | 'inactivo') => {
    try {
      const { error } = await supabase
        .from('usuarios')
        .update({ estado: newStatus })
        .eq('id', userId);

      if (error) throw error;
      setSuccess(`Usuario ${newStatus === 'activo' ? 'activado' : 'desactivado'} correctamente`);
      fetchUsers();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('¿Estás seguro de eliminar este usuario? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('usuarios')
        .delete()
        .eq('id', userId);

      if (error) throw error;
      setSuccess('Usuario eliminado correctamente');
      fetchUsers();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleGroupChange = (groupId: string) => {
    setNewUser({ ...newUser, grupo_id: groupId });
    const selectedGroup = userGroups.find(g => g.id === groupId);
    if (selectedGroup) {
      setSelectedGroupRole(selectedGroup.role);
    }
  };

  const filteredUsers = users.filter(user => {
    // Apply text search filter
    const matchesSearch = searchTerm === '' || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.apellidos.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.username?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    // Apply empresa filter
    const matchesEmpresa = empresaFilter === '' || 
      user.empresa?.nombre?.toLowerCase().includes(empresaFilter.toLowerCase());
    
    // Apply CIF filter
    const matchesCif = cifFilter === '' || 
      user.empresa?.codigo_fiscal?.toLowerCase().includes(cifFilter.toLowerCase());
    
    return matchesSearch && matchesEmpresa && matchesCif;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-full">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Gestión de Usuarios</h1>
        <button 
          onClick={() => {
            setEditingUser(null);
            setNewUser({
              email: '',
              username: '',
              password: '',
              nombre: '',
              apellidos: '',
              departamento_id: '',
              grupo_id: userGroups.find(g => g.nombre === 'Grupo Estándar')?.id || userGroups[0]?.id || '',
              empresa_id: ''
            });
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <UserPlus className="w-5 h-5 mr-2" />
          Añadir Usuario
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md mb-4">
        <div className="p-4 border-b flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[250px]">
            <input
              type="text"
              placeholder="Buscar por nombre, email o usuario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
          </button>
        </div>
        
        {showFilters && (
          <div className="p-4 bg-gray-50 border-b flex flex-wrap gap-4">
            <div className="flex-1 min-w-[250px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Empresa
              </label>
              <input
                type="text"
                placeholder="Filtrar por empresa"
                value={empresaFilter}
                onChange={(e) => setEmpresaFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex-1 min-w-[250px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CIF/NIF
              </label>
              <input
                type="text"
                placeholder="Filtrar por CIF/NIF"
                value={cifFilter}
                onChange={(e) => setCifFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left bg-gray-50">
                <th className="px-4 py-3 font-semibold text-gray-600">Nombre</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Usuario</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Email</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Departamento</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Grupo</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Empresa</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Rol</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Estado</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">{user.nombre} {user.apellidos}</td>
                  <td className="px-4 py-3">{user.username || '-'}</td>
                  <td className="px-4 py-3 max-w-[180px] truncate" title={user.email}>{user.email}</td>
                  <td className="px-4 py-3">{user.departamento?.nombre || '-'}</td>
                  <td className="px-4 py-3">{user.grupo?.nombre || '-'}</td>
                  <td className="px-4 py-3">
                    {user.empresa ? (
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 text-blue-500 mr-1 flex-shrink-0" />
                        <span className="truncate max-w-[150px]" title={user.empresa.nombre}>
                          {user.empresa.nombre}
                          {user.empresa.codigo_fiscal && (
                            <span className="text-xs text-gray-500 block">
                              {user.empresa.codigo_fiscal}
                            </span>
                          )}
                        </span>
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      user.role === 'administrador' 
                        ? 'bg-purple-100 text-purple-800'
                        : user.role === 'usuario_avanzado'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.role === 'administrador' 
                        ? 'Administrador'
                        : user.role === 'usuario_avanzado'
                        ? 'Usuario Avanzado'
                        : 'Usuario Estándar'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      user.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.estado === 'activo' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-1">
                      <button 
                        onClick={() => handleUpdateUserStatus(user.id, user.estado === 'activo' ? 'inactivo' : 'activo')}
                        className={`p-1 rounded-full ${
                          user.estado === 'activo' ? 'text-red-500 hover:bg-red-100' : 'text-green-500 hover:bg-green-100'
                        }`}
                        title={user.estado === 'activo' ? 'Desactivar usuario' : 'Activar usuario'}
                      >
                        {user.estado === 'activo' ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                      </button>
                      <button 
                        onClick={() => handleEdit(user)}
                        className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                        title="Editar usuario"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user.id)}
                        className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                        title="Eliminar usuario"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    No se encontraron usuarios que coincidan con los criterios de búsqueda
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 border-t bg-gray-50 text-sm text-gray-500">
          Mostrando {filteredUsers.length} de {users.length} usuarios
        </div>
      </div>

      {/* Modal para añadir/editar usuario */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form onSubmit={handleSubmit} className="space-y-4">
                {!editingUser && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="correo@ejemplo.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre de Usuario
                      </label>
                      <input
                        type="text"
                        required
                        value={newUser.username}
                        onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Nombre de usuario para iniciar sesión"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contraseña
                      </label>
                      <input
                        type="password"
                        required
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Contraseña para iniciar sesión"
                      />
                    </div>
                  </>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre
                    </label>
                    <input
                      type="text"
                      required
                      value={newUser.nombre}
                      onChange={(e) => setNewUser({...newUser, nombre: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Nombre del usuario"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apellidos
                    </label>
                    <input
                      type="text"
                      required
                      value={newUser.apellidos}
                      onChange={(e) => setNewUser({...newUser, apellidos: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Apellidos del usuario"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Departamento
                    </label>
                    <select
                      value={newUser.departamento_id}
                      onChange={(e) => setNewUser({...newUser, departamento_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Seleccionar departamento</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>{dept.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grupo
                    </label>
                    <select
                      value={newUser.grupo_id}
                      onChange={(e) => handleGroupChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Seleccionar grupo</option>
                      {userGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.nombre} ({group.role === 'administrador' 
                            ? 'Administrador' 
                            : group.role === 'usuario_avanzado'
                            ? 'Usuario Avanzado'
                            : 'Usuario Estándar'})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa
                  </label>
                  <select
                    value={newUser.empresa_id}
                    onChange={(e) => setNewUser({...newUser, empresa_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Sin empresa asignada</option>
                    {empresas.map((empresa) => (
                      <option key={empresa.id} value={empresa.id}>
                        {empresa.nombre} ({empresa.codigo_fiscal})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol (determinado por el grupo)
                  </label>
                  <div className={`px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600`}>
                    {selectedGroupRole === 'administrador' 
                      ? 'Administrador' 
                      : selectedGroupRole === 'usuario_avanzado'
                      ? 'Usuario Avanzado'
                      : 'Usuario Estándar'}
                  </div>
                </div>
              </form>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingUser(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingUser ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}